<template>
  <div>
    <b-row>
      <b-col md="12">
        <b-alert variant="warning" show dismissible>{{ $t('workInProgress') }}</b-alert>
      </b-col>
    </b-row>
    <b-row md="12">
      <!-- TODO Add filter for fetching based on EAN instead of license ID so user can see usage for overlapping licenses -->
      <b-col md="2">
        <b-form-group :label="$t('user')" label-for="user-filter" label-align-sm="right" label-cols-sm="4" label-size="sm">
          <b-form-input id="user-filter" v-model="filter.name" size="sm" type="search"/>
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-checkbox id="filter-include-deleted-users" size="sm" v-model="filter.includeDeletedUsers">
          {{ $t('includeDeletedUsers') }}
        </b-form-checkbox>
      </b-col>
      <b-col md="3">
        <b-form-checkbox id="filter-include-passive-usages" size="sm" v-model="filter.includePassiveUsages">
          {{ $t('includePassiveUsages') }}
        </b-form-checkbox>
      </b-col>
      <b-col md="4" class="text-right">
        <span v-if="totalFilteredRows != null">
          {{ $t('showing') }} {{ totalFilteredRows }} {{ $t('of') }} {{ response.length }} {{ totalFilteredRows !== 1 ? $t('rows') : $t('row') }}.
        </span>
        <span v-if="resultCount - response.length > 0" style="color: red">
          {{ resultCount - response.length }} {{ $t('notShown') }}.
          <font-awesome-icon id="notShownTip" icon="info-circle"/>
          <b-tooltip target="notShownTip">{{ $t('notShownTip') }}</b-tooltip>
        </span>
      </b-col>
    </b-row>

    <b-row>
      <b-col md="12" class="">
        <!-- FIXME Can sticky-header be used to fit table within modal 100%? No? https://github.com/bootstrap-vue/bootstrap-vue/issues/5842 -->
        <b-table id="license-usage-table" sticky-header="70vh"
                 striped hover class="aco-table" small primary-key="username"
                 :busy="isBusy"
                 :items="response"
                 :current-page="currentPage"
                 :per-page="perPage"
                 :fields="licenseUsageFields()"
                 :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" sort-icon-left
                 :filter="filter" :filter-function="filterRow" @filtered="onFiltered">
          <template #table-busy>
            <div class="text-center">
              <b-spinner class="align-middle"></b-spinner>
            </div>
          </template>
          <template #cell(fullname)="value">
            <span v-if="value.item.username == null"><i>{{ $t('deleted') }}</i></span>
            <span v-else>{{ value.item.fullName }}</span>
          </template>
        </b-table>
      </b-col>
      <b-col md="12">
        <!-- FIXME Show counts here, not above -->
        <b-pagination
          v-if="totalFilteredRows > perPage"
          v-model="currentPage"
          :total-rows="totalFilteredRows"
          :per-page="perPage"
          align="fill"
          size="sm"/>
      </b-col>
    </b-row>
  </div>
</template>

<script>
  import BaseComponent from '../../components/BaseComponent'
  import { mapGetters } from 'vuex'

  export default {
    name: 'LicenseUsages',
    extends: BaseComponent,
    props: [
      'licenseId'
    ],
    data () {
      return {
        sortBy: 'fullName',
        sortDesc: false,

        totalRows: 1,
        currentPage: 1,
        perPage: 100,

        filter: {
          name: null,
          includeDeletedUsers: false,
          includePassiveUsages: false
        },
        totalFilteredRows: null
      }
    },
    created () {
      this.$store.dispatch('licenseUsages/clear')
      // FIXME Looks like table is updated as response is loaded - can we delay the first render..?
      this.$store.dispatch('licenseUsages/getAllPaginated', {
        licenseId: this.licenseId
      })
    },
    computed: {
      ...mapGetters('licenseUsages', ['response', 'resultCount', 'isBusy']),
      ...mapGetters('languageStore', ['langCode']),
      totalLicenseUsageRows () {
        return this.licenseUsageFields.length
      }
    },
    methods: {
      licenseUsageFields () {
        return [
          { key: 'fullName', label: this.$t('name'), sortable: true },
          { key: 'username', label: this.$t('username'), sortable: true },
          { key: 'role', label: this.$t('role'), sortable: true, formatter: value => this.$t(`userPrimaryRoles.${value.toUpperCase()}`) },
          { key: 'firstAccess', label: this.$t('firstAccess'), sortable: true, tdClass: 'text-nowrap', formatter: 'formatDate' },
          { key: 'latestAccess', label: this.$t('latestAccess'), sortable: true, tdClass: 'text-nowrap', formatter: 'formatDate' },
          { key: 'accessCount', label: this.$t('accessCount'), sortable: true },
          { key: 'status', label: this.$t('status'), formatter: value => this.translateAlternatives('licenseUsageStatuses', value) },
        ]
      },
      filterRow (row, filter) {
        if (!filter.includeDeletedUsers && !row.username) return false
        if (!filter.includePassiveUsages && row.status !== 'ACTIVE') return false
        if (filter.name) {
          let name = filter.name.toLowerCase()
          return (row.username && row.username.toLowerCase().includes(name) || (row.fullName && row.fullName.toLowerCase().includes(name)))
        }

        return true
      },
      onFiltered (filteredItems) {
        // Trigger pagination to update the number of buttons/pages due to filtering
        this.totalFilteredRows = filteredItems.length
        this.currentPage = 1
      }
    },
    i18n: {
      messages: {
        en: {
          'licenseUsage': 'License usage',
          'firstAccess': 'First used',
          'latestAccess': 'Last used',
          'accessCount': 'Access count',
          'deleted': 'Deleted',
          'includeDeletedUsers': 'Include deleted users',
          'includePassiveUsages': 'Include passive usage',
          'of': 'of',
          'row': 'row',
          'rows': 'rows',
          'notShown': 'results not fetched',
          'notShownTip': 'For performance reasons, a maximum of 1000 hits are returned. This means data is missing in the table.',
          'showing': 'Showing',
          'workInProgress': 'Work in progress!'
        },
        nb: {
          'licenseUsage': 'Lisensbruk',
          'firstAccess': 'Først brukt',
          'latestAccess': 'Sist brukt',
          'accessCount': 'Bruksteller',
          'deleted': 'Slettet',
          'includeDeletedUsers': 'Inkluder slettede brukere',
          'includePassiveUsages': 'Inkluder passiv bruk',
          'of': 'av',
          'row': 'rad',
          'rows': 'rader',
          'notShown': 'resultater ikke hentet',
          'notShownTip': 'Av ytelseshensyn hentes maksimum 1000 treff. Dette betyr at det mangler data i tabellen.',
          'showing': 'Viser',
          'workInProgress': 'Under arbeid!'
        }
      }
    }
  }
</script>